import * as JSONMapper from "json-mapper";
import {jsonProperty} from "json-mapper-reflect";
import {AvdbType} from "./avdb-type";
import {Message} from "./common/message";
import {DeviceAvdbInstalledIssue} from "./device-avdb-installed-issue";
import {DeviceAvdbSeries} from "./device-avdb-series";

export class DeviceAvdbType extends AvdbType {
	@jsonProperty()
	daysPerCycle: number;

	@jsonProperty()
	deviceId: number;

	@jsonProperty()
	status: string;

	@jsonProperty(JSONMapper.arrayOfClass(DeviceAvdbSeries))
	series: Array<DeviceAvdbSeries>;

	@jsonProperty()
	installedIssue: DeviceAvdbInstalledIssue;

	@jsonProperty(JSONMapper.arrayOfClass(DeviceAvdbInstalledIssue))
	installedIssuesByRegion: Array<DeviceAvdbInstalledIssue>;

	@jsonProperty(JSONMapper.arrayOfClass(Message))
	messages: Array<Message>;

	constructor(data?: Partial<DeviceAvdbType>) {
		super();
		Object.assign(this, data);
	}

	get installableSeries() {
		return this.series.filter(series => series.installableIssues.length > 0);
	}

	get installedSeries() {
		if (this.installedIssue == null)
			return null;
		return this.series.find(series => series.id === this.installedIssue.seriesID);
	}

	get hasPurchaseableSeries() {
		// TODO: This isn't very reliable. If the customer has never purchased anything, there won't be
		// any series at all. If there is a free database, there will be at least one series. This
		// doesn't cover cases where there is a free series plus some paid options that haven't been
		// purchased.
		return this.series.length === 0 || this.series.some(series => !series.isFree);
	}

	/**
	 * This legacy method is used to check if the the specified series is the installed issue series.
	 */
	isInstalledSeries(seriesID: number) {
		if (this.installedIssue == null)
			return false;
		return this.installedIssue.seriesID === seriesID;
	}

	/**
	 * This method replaces the above method to determine if the specified series is in the list of installedIssuesByRegion.
	 */
	isAnInstalledSeries(seriesId: number) {
		if (this.installedIssuesByRegion == null) {
			// Fallback on singular version for older style objects
			return this.isInstalledSeries(seriesId);
		}
		return this.installedIssuesByRegion.filter(issue => issue.seriesID == seriesId).length > 0;
	}

	/**
	 * This legacy method is used to check if the the specified issue is the installed issue.
	 */
	isInstalledIssue(seriesID: number, issueName: string) {
		return this.isInstalledSeries(seriesID) && this.installedIssue.name === issueName;
	}

	/**
	 * This method replaces the above method to determine if the specified issue is in the list of installedIssuesByRegion.
	 */
	isAnInstalledIssue(seriesId: number, issueName: string) {
		if (this.installedIssuesByRegion == null) {
			// Fallback on singular version for older style objects
			return this.isInstalledIssue(seriesId, issueName);
		}
		return this.installedIssuesByRegion.filter(issue => issue.seriesID == seriesId && issue.name === issueName).length > 0;
	}
}

export enum DeviceAvdbTypeStatus {
	NOT_INSTALLED = "NotInstalled",
	LATEST        = "Latest",
	OUTDATED      = "Outdated",
	EXPIRED       = "Expired",
}
